import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { SiteNav } from '@/components/SiteNav';
import { SiteFooter } from '@/components/SiteFooter';
import { useCart } from '@/contexts/CartContext';
import { getSettings } from '@/lib/store';
import { useSeoMeta } from '@unhead/react';

type PaymentStatus = 'idle' | 'loading' | 'success' | 'error';

declare global {
  interface Window {
    paypal?: {
      Buttons: (opts: Record<string, unknown>) => { render: (sel: string) => Promise<void> };
    };
    Square?: {
      payments: (appId: string, locationId: string) => Promise<{
        card: () => Promise<{
          attach: (sel: string) => Promise<void>;
          tokenize: () => Promise<{ status: string; token: string }>;
        }>;
      }>;
    };
  }
}

function loadScript(src: string, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) { resolve(); return; }
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

export function CheckoutPage() {
  useSeoMeta({ title: 'Checkout — Dakota Horizon Outdoors' });

  const { cartProducts, totalPrice, totalItems, clearCart } = useCart();
  const navigate = useNavigate();
  const settings = getSettings();
  const [status, setStatus] = useState<PaymentStatus>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [activeTab, setActiveTab] = useState<'paypal' | 'square' | 'card'>(
    settings.checkoutProvider === 'square' ? 'square' : 'paypal'
  );
  const paypalRendered = useRef(false);
  const squareCard = useRef<{ tokenize: () => Promise<{ status: string; token: string }> } | null>(null);

  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    address: '', city: '', state: '', zip: '', country: 'US',
  });

  const shipping = totalPrice >= 75 ? 0 : 9.99;
  const tax = totalPrice * 0.07;
  const orderTotal = totalPrice + shipping + tax;

  // Load PayPal SDK
  useEffect(() => {
    if (!settings.paypalClientId || activeTab !== 'paypal' || paypalRendered.current) return;
    const src = `https://www.paypal.com/sdk/js?client-id=${settings.paypalClientId}&currency=${settings.storeCurrency || 'USD'}`;
    loadScript(src, 'paypal-sdk').then(() => {
      if (!window.paypal) return;
      paypalRendered.current = true;
      window.paypal.Buttons({
        createOrder: (_data: unknown, actions: Record<string, Function>) => {
          return actions.order.create({
            purchase_units: [{
              amount: { value: orderTotal.toFixed(2), currency_code: settings.storeCurrency || 'USD' },
              description: `Dakota Horizon Outdoors — ${totalItems} item${totalItems !== 1 ? 's' : ''}`,
            }],
          });
        },
        onApprove: (_data: unknown, actions: Record<string, Function>) => {
          setStatus('loading');
          return actions.order.capture().then(() => {
            clearCart();
            setStatus('success');
          });
        },
        onError: (err: unknown) => {
          console.error('PayPal error', err);
          setErrorMsg('PayPal payment failed. Please try again.');
          setStatus('error');
        },
      }).render('#paypal-button-container');
    }).catch(() => {
      setErrorMsg('Failed to load PayPal. Check your Client ID in Admin Settings.');
      setStatus('error');
    });
  }, [settings.paypalClientId, activeTab, orderTotal, totalItems, clearCart, settings.storeCurrency]);

  // Load Square Web Payments SDK
  useEffect(() => {
    if (!settings.squareAppId || !settings.squareLocationId || activeTab !== 'square') return;
    const src = settings.squareMode === 'production'
      ? 'https://web.squarecdn.com/v1/square.js'
      : 'https://sandbox.web.squarecdn.com/v1/square.js';
    loadScript(src, 'square-sdk').then(async () => {
      if (!window.Square) return;
      const payments = await window.Square.payments(settings.squareAppId, settings.squareLocationId);
      const card = await payments.card();
      await card.attach('#square-card-container');
      squareCard.current = card;
    }).catch(() => {
      setErrorMsg('Failed to load Square. Check your credentials in Admin Settings.');
    });
  }, [settings.squareAppId, settings.squareLocationId, settings.squareMode, activeTab]);

  async function handleSquareSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!squareCard.current) return;
    setStatus('loading');
    try {
      const result = await squareCard.current.tokenize();
      if (result.status === 'OK') {
        // In production, send result.token to your backend/Square API
        console.log('Square token:', result.token);
        clearCart();
        setStatus('success');
      } else {
        throw new Error('Tokenization failed');
      }
    } catch (err) {
      setErrorMsg('Payment failed. Please try again.');
      setStatus('error');
    }
  }

  if (totalItems === 0 && status !== 'success') {
    return (
      <div className="min-h-screen bg-background">
        <SiteNav />
        <main className="pt-24 container mx-auto px-4 py-20 text-center">
          <ShoppingBag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Your cart is empty</h1>
          <p className="text-muted-foreground mb-6">Add some gear before checking out.</p>
          <Button asChild className="bg-amber-700 hover:bg-amber-600 text-white rounded-sm">
            <Link to="/">Continue Shopping</Link>
          </Button>
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-background">
        <SiteNav />
        <main className="pt-24 container mx-auto px-4 py-20 text-center max-w-md mx-auto">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-foreground mb-3">Order Confirmed!</h1>
          <p className="text-muted-foreground mb-2">Thank you for your order. A confirmation email will be sent to <strong>{form.email || 'your email'}</strong>.</p>
          <p className="text-muted-foreground text-sm mb-8">We'll get your gear shipped out as soon as possible.</p>
          <Button asChild className="bg-amber-700 hover:bg-amber-600 text-white rounded-sm">
            <Link to="/">Back to Store</Link>
          </Button>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const noPaymentConfigured = !settings.paypalClientId && !settings.squareAppId;

  return (
    <div className="min-h-screen bg-background">
      <SiteNav />
      <main className="pt-20 pb-12">
        <div className="container mx-auto px-4 py-8">
          <Link to="/" className="inline-flex items-center gap-1 text-muted-foreground hover:text-amber-600 text-sm mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4" /> Continue Shopping
          </Link>
          <h1 className="text-3xl font-bold text-foreground mb-8">Checkout</h1>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Left: Forms */}
            <div className="lg:col-span-3 space-y-6">
              {/* Contact */}
              <Card className="border-border/50">
                <CardHeader><CardTitle className="text-base">Contact Information</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label>First Name</Label>
                      <Input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} placeholder="John" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Last Name</Label>
                      <Input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} placeholder="Doe" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Email</Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="john@example.com" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Phone <span className="text-muted-foreground text-xs">optional</span></Label>
                    <Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 555-5555" />
                  </div>
                </CardContent>
              </Card>

              {/* Shipping */}
              <Card className="border-border/50">
                <CardHeader><CardTitle className="text-base">Shipping Address</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-1.5">
                    <Label>Street Address</Label>
                    <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="123 Main St" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label>City</Label>
                      <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Bismarck" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>State</Label>
                      <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="ND" maxLength={2} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label>ZIP Code</Label>
                      <Input value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} placeholder="58501" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Country</Label>
                      <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="US" maxLength={2} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Payment */}
              <Card className="border-border/50">
                <CardHeader><CardTitle className="text-base">Payment</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  {noPaymentConfigured && (
                    <div className="flex items-start gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/30 rounded-md p-4">
                      <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-amber-800 dark:text-amber-300 font-medium text-sm">Payment not configured</p>
                        <p className="text-amber-700 dark:text-amber-400 text-xs mt-0.5">
                          Add your PayPal or Square credentials in the{' '}
                          <Link to="/admin/settings" className="underline hover:text-amber-600">Admin Settings</Link>.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Tab switcher */}
                  {!noPaymentConfigured && (
                    <div className="flex gap-2">
                      {settings.paypalClientId && (settings.checkoutProvider === 'paypal' || settings.checkoutProvider === 'both') && (
                        <button
                          type="button"
                          onClick={() => setActiveTab('paypal')}
                          className={`flex-1 py-2 text-sm rounded border transition-colors font-medium ${
                            activeTab === 'paypal' ? 'border-amber-600 bg-amber-700/10 text-amber-700' : 'border-border text-muted-foreground hover:border-foreground'
                          }`}
                        >
                          PayPal
                        </button>
                      )}
                      {settings.squareAppId && (settings.checkoutProvider === 'square' || settings.checkoutProvider === 'both') && (
                        <button
                          type="button"
                          onClick={() => setActiveTab('square')}
                          className={`flex-1 py-2 text-sm rounded border transition-colors font-medium ${
                            activeTab === 'square' ? 'border-amber-600 bg-amber-700/10 text-amber-700' : 'border-border text-muted-foreground hover:border-foreground'
                          }`}
                        >
                          Credit / Debit Card
                        </button>
                      )}
                    </div>
                  )}

                  {/* PayPal button */}
                  {activeTab === 'paypal' && settings.paypalClientId && (
                    <div>
                      <div id="paypal-button-container" className="min-h-[45px]" />
                      {status === 'loading' && (
                        <div className="flex items-center gap-2 mt-3 text-muted-foreground text-sm">
                          <Loader2 className="h-4 w-4 animate-spin" /> Processing payment…
                        </div>
                      )}
                    </div>
                  )}

                  {/* Square card form */}
                  {activeTab === 'square' && settings.squareAppId && (
                    <form onSubmit={handleSquareSubmit} className="space-y-4">
                      <div className="space-y-1.5">
                        <Label>Card Details</Label>
                        <div
                          id="square-card-container"
                          className="border border-border rounded-md p-3 min-h-[60px] bg-background"
                        />
                      </div>
                      {status === 'error' && (
                        <p className="text-red-500 text-sm flex items-center gap-1.5">
                          <AlertCircle className="h-4 w-4" /> {errorMsg}
                        </p>
                      )}
                      <Button
                        type="submit"
                        disabled={status === 'loading'}
                        className="w-full bg-amber-700 hover:bg-amber-600 text-white font-semibold h-12 rounded-sm"
                      >
                        {status === 'loading' ? (
                          <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Processing…</>
                        ) : (
                          `Pay $${orderTotal.toFixed(2)}`
                        )}
                      </Button>
                    </form>
                  )}

                  {status === 'error' && activeTab === 'paypal' && (
                    <p className="text-red-500 text-sm flex items-center gap-1.5">
                      <AlertCircle className="h-4 w-4" /> {errorMsg}
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Right: Order summary */}
            <div className="lg:col-span-2">
              <Card className="border-border/50 sticky top-24">
                <CardHeader><CardTitle className="text-base">Order Summary</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  {/* Items */}
                  <div className="space-y-3">
                    {cartProducts.map(({ product, quantity }) => (
                      <div key={product.id} className="flex items-center gap-3">
                        <div className="h-12 w-12 rounded-md bg-muted flex items-center justify-center overflow-hidden shrink-0 relative">
                          {product.imageUrl ? (
                            <img src={product.imageUrl} alt={product.name} className="h-full w-full object-cover" />
                          ) : (
                            <img src="/images/DHO.jpg" alt="DHO" className="h-8 w-8 object-contain opacity-20" />
                          )}
                          <Badge className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-amber-700 text-white text-[10px] p-0 flex items-center justify-center border-0">
                            {quantity}
                          </Badge>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground line-clamp-1">{product.name}</p>
                          <p className="text-xs text-muted-foreground capitalize">{product.category}</p>
                        </div>
                        <span className="text-sm font-semibold">${(product.price * quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>

                  <Separator />

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between text-muted-foreground">
                      <span>Subtotal</span>
                      <span>${totalPrice.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Shipping</span>
                      <span>{shipping === 0 ? <span className="text-green-600 font-medium">Free</span> : `$${shipping.toFixed(2)}`}</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Tax (est.)</span>
                      <span>${tax.toFixed(2)}</span>
                    </div>
                  </div>

                  <Separator />

                  <div className="flex justify-between font-bold text-base">
                    <span>Total</span>
                    <span className="text-amber-700">${orderTotal.toFixed(2)}</span>
                  </div>

                  {shipping > 0 && (
                    <p className="text-xs text-muted-foreground bg-amber-50 dark:bg-amber-900/20 rounded px-3 py-2">
                      Add ${(75 - totalPrice).toFixed(2)} more for free shipping!
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

export default CheckoutPage;
